import { useState, type ReactNode } from 'react';
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Link from '@mui/material/Link';
import Skeleton from '@mui/material/Skeleton';
import Snackbar from '@mui/material/Snackbar';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import type { InstanceApi } from '@eis/insuranceservice-sdk';
import { civilToday, formatCivilDate, isApiError, links, resolveFormattingLocale } from '@eis/core';
import {
  ExportButton,
  DetailsSection,
  EntityHeader,
  ErnLine,
  ErrorState,
  PageLayout,
  RequirePermission,
  usePermissions,
} from '@eis/patterns';
import { insurancePermissions as p } from '../../../permissions';
import { buildPolicyExportLabels } from '../model/export-labels';
import { usePolicy, usePolicySchema, usePolicyTimeline } from '../api/policies';
import {
  insuredLabelOf,
  isInheritedPolicyholder,
  isMember,
  policyStatusOf,
  policyholderLabelOf,
} from '../model/types';
import { PartnerList } from './PartnerList';
import { CorrectDialog } from './CorrectDialog';
import { DetachDialog, TerminateDialog } from './DetachDialog';
import { UpgradeDialog } from './UpgradeDialog';
import { EndorseDialog } from './EndorseDialog';
import { PolicyTimelineTab } from './PolicyTimelineTab';
import { PolicyBillingTab } from './PolicyBillingTab';
import { PolicyClaimsTab } from './PolicyClaimsTab';
import { PolicyPremiumSection } from '../../premium/ui/PremiumSection';
import { PolicyCoverageCard } from './PolicyCoverageCard';
import { PolicyCutBar, type PolicyCutChange } from './PolicyCutBar';
import { flattenData } from '../../leads/model/leads';

export type PolicyTab = 'overview' | 'timeline' | 'billing' | 'claims';

/** The first day any segment of the timeline speaks for — the policy's start. */
function earliestStart(
  segments: readonly InstanceApi.PolicySegment[] | undefined,
): string | undefined {
  const dates = (segments ?? [])
    .map((segment) => segment.snapshot?.validFrom ?? '')
    .filter((date) => date !== '')
    .sort();
  return dates[0];
}

type OpenDialog = 'endorse' | 'correct' | 'terminate' | 'detach' | 'upgrade' | null;

/**
 * One policy, as the operations persona reads it.
 *
 * # The header states which belief this is
 *
 * A bitemporal store can answer several questions about one policy, so a screen that showed a
 * payload without saying which belief it is showing would be ambiguous by construction. The
 * header therefore carries the snapshot envelope — the period it is in force for, and the
 * business version — in the persona's own words: "Stand" and "Gültig", never "Version"
 * (SSOT I-6). The number is still there, because two operators editing one policy need to be
 * talking about the same one.
 *
 * # The four actions are four operations, not one form
 *
 * Endorse extends the timeline. Correct re-states it. Terminate ends cover on a date. Detach
 * promotes a role. They are separate because each has a different effect on history and a
 * different audit consequence — and each carries its own permission for the same reason.
 */
export function PolicyDetailPage({
  code,
  tab,
  onTabChange,
  fromLead,
  validAt,
  asOf,
  hl,
  onCutChange,
}: {
  readonly code: string;
  readonly tab: PolicyTab;
  readonly onTabChange: (tab: PolicyTab) => void;
  /** Set by AcceptLead's navigation (SDD-013 §8.1.3): the toast that links back to the lead. */
  readonly fromLead?: string;
  /**
   * The civil date the belief is read for; today when absent (SDD-008 §5). A policy that starts in
   * the future — every policy accepted from a lead is one on the day it is accepted — has no belief
   * about today, so a link from a lead carries the start (SDD-013 R-4).
   */
  readonly validAt?: string;
  /** The system time (`…Z`) the belief is read as recorded at — the audit read; now when absent. */
  readonly asOf?: string;
  /** A catalog key to highlight (`data.bike.insurance_plate`): the field a search hit matched. */
  readonly hl?: string;
  /** The timeline strip and the audit toggle write the cut back to the URL. */
  readonly onCutChange?: (change: PolicyCutChange) => void;
}): ReactNode {
  const { t, i18n } = useTranslation('insurance');
  const locale = resolveFormattingLocale(i18n.language);
  const navigate = useNavigate();
  // A read is the belief about `asOf` — today when absent (SDD-008 §5). A policy that starts
  // later has none about today; rather than a dead end, the page asks the timeline (which knows
  // every segment) for the start and reads as of THAT, saying so. A link that already carries
  // the date (from a lead, SDD-013 R-4) never needs the detour.
  const asRequested = usePolicy(code, validAt, asOf);
  const startsLater =
    validAt === undefined &&
    asRequested.isError &&
    isApiError(asRequested.error) &&
    asRequested.error.status === 404;
  const timeline = usePolicyTimeline(code, { enabled: startsLater });
  const start = startsLater ? earliestStart(timeline.data?.segments) : undefined;
  const effectiveValidAt = validAt ?? start;
  const atStart = usePolicy(code, start ?? validAt, asOf);
  const policyQuery = start === undefined ? asRequested : atStart;
  // The pinned tarif's roles, for the role names the partner list and the dialogs show (P-8).
  const schemaQuery = usePolicySchema(code, effectiveValidAt);
  const [dialog, setDialog] = useState<OpenDialog>(null);
  const [toastOpen, setToastOpen] = useState(fromLead !== undefined && fromLead !== '');
  // The billing tab (SDD-015 §7.2) is a SECTION of this route, gated by the billing domain's own
  // `invoices.view` (a typed reference, PERMISSIONS.md): a reader who may see the policy and not
  // its money sees no tab — never a 403 on the policy.
  const { can } = usePermissions();
  const canSeeBilling = can(p.invoices.view);
  // The claims tab (SDD-018 §9, prompt 26 phase 26.7) likewise: claimservice's data, gated by the
  // claims domain's own `claims.view` (a typed reference, PERMISSIONS.md).
  const canSeeClaims = can(p.claims.view);
  const activeTab: PolicyTab =
    (tab === 'billing' && !canSeeBilling) || (tab === 'claims' && !canSeeClaims) ? 'overview' : tab;

  // Still looking for the start: the belief about today is gone, the timeline not yet read.
  if (startsLater && (timeline.isPending || (start !== undefined && atStart.isPending))) {
    return (
      <PageLayout header={<Skeleton variant="text" width={320} height={44} />}>
        <Skeleton variant="rounded" height={260} />
      </PageLayout>
    );
  }

  if (policyQuery.isError) {
    // Not found without a date, and no start the timeline could name: the one refusal with a
    // likely cause the reader can act on (a policy that begins later, SDD-013 R-4).
    const notFoundToday =
      validAt === undefined && isApiError(policyQuery.error) && policyQuery.error.status === 404;
    return (
      <PageLayout>
        <ErrorState
          description={
            notFoundToday ? t('policies.error.notFoundToday') : t('policies.error.detail')
          }
          details={policyQuery.error.message}
          onRetry={() => {
            void policyQuery.refetch();
          }}
        />
      </PageLayout>
    );
  }

  if (policyQuery.data === undefined) {
    return (
      <PageLayout header={<Skeleton variant="text" width={320} height={44} />}>
        <Skeleton variant="rounded" height={260} />
      </PageLayout>
    );
  }

  const policy = policyQuery.data.policy;
  const snapshot = policyQuery.data.snapshot;
  const status = policyStatusOf(policy);
  const member = isMember(policy);

  const closeDialog = (): void => {
    setDialog(null);
  };

  return (
    <PageLayout
      header={
        <EntityHeader
          title={policy?.code ?? code}
          subtitle={policy?.insuredPerson?.partnerId}
          status={{ value: status, label: t(`policies.status.${status}` as never) }}
          icon={<DescriptionOutlinedIcon />}
          // Berry has no breadcrumb band: the way back is the header's own link (prompt 24).
          backTo={{
            label: t('policies.title'),
            href: '/insurance/policies',
            onNavigate: (event) => {
              event.preventDefault();
              void navigate({ to: '/insurance/policies' });
            },
          }}
          meta={[
            {
              // The copyable identity (T-23): the ERN is what crosses a service boundary; the
              // code above is what a human quotes.
              label: t('policies.ern'),
              value: <ErnLine ern={policy?.ern} copyLabel={t('policies.ernCopy')} />,
            },
            {
              label: t('policies.snapshot.validFrom'),
              value:
                snapshot?.validFrom === undefined || snapshot.validFrom === ''
                  ? '—'
                  : formatCivilDate(snapshot.validFrom, locale),
            },
            {
              label: t('policies.snapshot.validTo'),
              value:
                snapshot?.validTo === undefined || snapshot.validTo === ''
                  ? t('policies.snapshot.open')
                  : formatCivilDate(snapshot.validTo, locale),
            },
            // The number two operators need to be talking about the same belief. Labelled
            // "Stand", because the operations persona has no versions (SSOT I-6).
            { label: t('policies.snapshot.state'), value: snapshot?.businessVersion },
            ...((policy?.originLeadCode ?? '') !== ''
              ? [
                  {
                    // R-4 (SDD-013): the policy's half of the two-way link. A link for whoever may
                    // see leads; the code as text for everybody else.
                    label: t('leads.singular'),
                    value: (
                      <RequirePermission
                        anyOf={[p.leads.view]}
                        fallback={
                          <span data-testid="origin-lead">
                            {t('leads.link.createdFromLead', {
                              code: policy?.originLeadCode ?? '',
                            })}
                          </span>
                        }
                      >
                        <Link
                          data-testid="origin-lead"
                          href={links.insurance.lead(policy?.originLeadCode ?? '')}
                          onClick={(event) => {
                            event.preventDefault();
                            void navigate({
                              href: links.insurance.lead(policy?.originLeadCode ?? ''),
                            });
                          }}
                        >
                          {t('leads.link.createdFromLead', { code: policy?.originLeadCode ?? '' })}
                        </Link>
                      </RequirePermission>
                    ),
                  },
                ]
              : []),
          ]}
          actions={
            <Stack direction="row" spacing={1}>
              {/* The policy as one workbook at the date on screen (SDD-020 §8.3). */}
              <ExportButton
                service="insurance"
                permission={p.exports.create}
                buildDetailRequest={() => ({
                  resource: policy?.ern ?? `insuranceservice:policy/${code}`,
                  ...(effectiveValidAt === undefined ? {} : { validAt: effectiveValidAt }),
                  labels: buildPolicyExportLabels(t),
                  label: t('policies.exportLabel', { code }),
                })}
              />
              <RequirePermission anyOf={[p.policies.update]}>
                <Button
                  variant="contained"
                  onClick={() => {
                    setDialog('endorse');
                  }}
                >
                  {t('policies.endorse.action')}
                </Button>
              </RequirePermission>
              <RequirePermission anyOf={[p.policies.correct]}>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setDialog('correct');
                  }}
                >
                  {t('policies.correct.action')}
                </Button>
              </RequirePermission>
              <RequirePermission anyOf={[p.policies.terminate]}>
                <Button
                  variant="outlined"
                  color="warning"
                  onClick={() => {
                    setDialog('terminate');
                  }}
                >
                  {t('policies.terminate.action')}
                </Button>
              </RequirePermission>
              {/* An upgrade IS an endorsement (SDD-016 §5), so it is `policies.update`. */}
              <RequirePermission anyOf={[p.policies.update]}>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setDialog('upgrade');
                  }}
                >
                  {t('policies.upgrade.action')}
                </Button>
              </RequirePermission>
              {member && (
                <RequirePermission anyOf={[p.policies.detach]}>
                  <Button
                    variant="outlined"
                    color="warning"
                    onClick={() => {
                      setDialog('detach');
                    }}
                  >
                    {t('policies.detach.action')}
                  </Button>
                </RequirePermission>
              )}
            </Stack>
          }
        />
      }
      subHeader={
        <Tabs
          value={activeTab}
          onChange={(_event, next: PolicyTab) => {
            onTabChange(next);
          }}
          aria-label={t('policies.tabs.label')}
          sx={{ width: '100%' }}
        >
          <Tab value="overview" label={t('policies.tabs.overview')} />
          <Tab value="timeline" label={t('policies.tabs.timeline')} />
          {canSeeBilling && <Tab value="billing" label={t('policies.tabs.billing')} />}
          {canSeeClaims && <Tab value="claims" label={t('policies.tabs.claims')} />}
        </Tabs>
      }
    >
      {activeTab === 'billing' && canSeeBilling ? (
        <PolicyBillingTab code={code} locale={locale} />
      ) : activeTab === 'claims' && canSeeClaims ? (
        <PolicyClaimsTab code={code} locale={locale} />
      ) : activeTab === 'overview' ? (
        <Stack spacing={2.5}>
          {onCutChange !== undefined && (
            <PolicyCutBar
              code={code}
              shownValidFrom={snapshot?.validFrom}
              asOf={asOf}
              locale={locale}
              onChange={onCutChange}
            />
          )}
          {start !== undefined && (
            <Alert severity="info" data-testid="starts-later">
              {t('policies.startsLater', { date: formatCivilDate(start, locale) })}
            </Alert>
          )}
          <DetailsSection
            title={t('policies.overview.parties')}
            fields={[
              { label: t('policies.overview.insured'), value: insuredLabelOf(policy) },
              {
                label: t('policies.overview.policyholder'),
                /**
                 * The inheritance marker again, and it says more here than in the list: this is
                 * the screen where somebody would try to change it. They cannot — it belongs to
                 * the Gruppen-Police and is resolved at read (decision D7) — and the chip is
                 * how they find that out before looking for an edit button.
                 */
                value: (
                  <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                    <span>{policyholderLabelOf(policy)}</span>
                    {isInheritedPolicyholder(policy) && (
                      <Chip
                        size="small"
                        variant="outlined"
                        label={t('policies.list.inherited')}
                        data-testid="inherited-marker"
                      />
                    )}
                  </Stack>
                ),
              },
              ...(member
                ? [
                    {
                      label: t('policies.overview.groupPolicy'),
                      value: policy?.parentPolicyCode,
                    },
                  ]
                : []),
              {
                // Every party per role, resolved (SDD-011): the name when the directory knows one,
                // the id marked unresolved when not. The two fields above are its projections.
                label: t('policies.partners.perRole'),
                wide: true,
                value: (
                  <PartnerList partners={policy?.partners} rules={schemaQuery.data?.partnerRoles} />
                ),
              },
            ]}
          />

          <DetailsSection
            title={t('policies.overview.cover')}
            fields={[
              {
                label: t('policies.overview.tarif'),
                value: policy?.configuration?.resolvedTarifVersionErn,
              },
            ]}
            advancedFields={[
              { label: t('policies.overview.digest'), value: policy?.configuration?.digest },
            ]}
          />

          {/*
            The *Prämie* (SDD-021 §9): what the policy costs — net, tax per code and gross, actual
            beside estimated — and what the premium is made of. It replaces the cover section's
            premium lines; the estimate is asked for the page's own business day, after the page.
            For a member, the note that group-level adjustments live on the group policy.
          */}
          <PolicyPremiumSection
            code={code}
            validAt={effectiveValidAt}
            premium={policy?.premium}
            {...(member ? { groupPolicyCode: policy?.parentPolicyCode ?? '' } : {})}
            locale={locale}
          />

          {/* What the policy covers at this cut (SDD-018 §7): the claims question, asked of the page's date. */}
          <PolicyCoverageCard
            code={code}
            validAt={policyQuery.data?.validAt ?? effectiveValidAt ?? civilToday('UTC')}
            {...(asOf === undefined ? {} : { asOf })}
            locale={locale}
          />

          {/*
            The payload as label/value rows, one per LEAF — `bike.bike_value`, `cover.coverage_tier` —
            the same flattening the lead's overview uses. The schema groups its fields into objects,
            and a row per group would print "[object Object]" (found 2026-09-21).
          */}
          <DetailsSection
            title={t('policies.overview.data')}
            fields={Object.entries(flattenData(policy?.data)).map(([key, value]) => ({
              label: key,
              value: value === '' ? '—' : value,
              // `?hl=` names a catalog key (`data.bike.insurance_plate`); the rows are its path.
              highlighted: hl?.replace(/^data\./, '').replace(/\[\]/g, '') === key,
            }))}
          />
        </Stack>
      ) : (
        <Box>
          <PolicyTimelineTab code={code} rules={schemaQuery.data?.partnerRoles} />
        </Box>
      )}

      <RequirePermission anyOf={[p.policies.update]}>
        <EndorseDialog
          code={code}
          policy={policy}
          open={dialog === 'endorse'}
          onClose={closeDialog}
        />
      </RequirePermission>
      <RequirePermission anyOf={[p.policies.correct]}>
        <CorrectDialog
          code={code}
          policy={policy}
          open={dialog === 'correct'}
          onClose={closeDialog}
        />
      </RequirePermission>
      <RequirePermission anyOf={[p.policies.terminate]}>
        <TerminateDialog code={code} open={dialog === 'terminate'} onClose={closeDialog} />
      </RequirePermission>
      <RequirePermission anyOf={[p.policies.update]}>
        <UpgradeDialog
          kind="policy"
          code={code}
          currentVersionErn={policy?.configuration?.resolvedTarifVersionErn ?? ''}
          open={dialog === 'upgrade'}
          ready={!policyQuery.isFetching}
          onClose={closeDialog}
        />
      </RequirePermission>
      <RequirePermission anyOf={[p.policies.detach]}>
        <DetachDialog
          code={code}
          policy={policy}
          rules={schemaQuery.data?.partnerRoles}
          open={dialog === 'detach'}
          onClose={closeDialog}
        />
      </RequirePermission>

      {/* AcceptLead landed here (SDD-013 §8.1.3): say so, and offer the way back to the lead. */}
      <Snackbar
        open={toastOpen}
        autoHideDuration={8000}
        onClose={() => {
          setToastOpen(false);
        }}
        message={t('leads.accept.created', { code: policy?.code ?? code })}
        action={
          <RequirePermission anyOf={[p.leads.view]}>
            <Button
              color="inherit"
              size="small"
              onClick={() => {
                void navigate({ href: links.insurance.lead(fromLead ?? '') });
              }}
            >
              {t('leads.accept.backToLead')}
            </Button>
          </RequirePermission>
        }
      />
    </PageLayout>
  );
}
